import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  ClipboardText, CheckCircle, XCircle, Folder, FileText,
  ArrowRight, Warning, Seal, RocketLaunch, Clock, EnvelopeSimple
} from '@phosphor-icons/react';
import { Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import api, { getPortalOnboardingNotifications } from '../services/api';

const STEP_ICONS = {
  adhesion: ClipboardText,
  documents: FileText,
  validation: Seal,
  activation: RocketLaunch,
};

const STATUS_STYLES = {
  completed: { bg: 'bg-emerald-500', ring: 'ring-emerald-200', text: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-700' },
  in_progress: { bg: 'bg-amber-500', ring: 'ring-amber-200', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-700' },
  pending: { bg: 'bg-gray-300', ring: 'ring-gray-100', text: 'text-gray-500', badge: 'bg-gray-100 text-gray-500' },
};

const STATUS_LABELS = {
  completed: 'Termine',
  in_progress: 'En cours',
  pending: 'A faire',
};

export function PortalOnboardingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedDossier, setExpandedDossier] = useState(null);
  const [myNotifications, setMyNotifications] = useState([]);

  useEffect(() => {
    const fetchOnboarding = async () => {
      try {
        const [res, notifRes] = await Promise.all([
          api.get('/portal/onboarding'),
          getPortalOnboardingNotifications(10),
        ]);
        setData(res.data);
        setMyNotifications(notifRes.data.items || []);
      } catch {
        toast.error('Erreur de chargement');
      } finally {
        setLoading(false);
      }
    };
    fetchOnboarding();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-[#2D5A4C]" />
      </div>
    );
  }

  if (!data || !data.steps || data.steps.length === 0) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold text-[#1A2421]" style={{ fontFamily: 'Manrope' }} data-testid="portal-onboarding-title">
            Mon Onboarding
          </h1>
        </div>
        <Card className="border-[#E8E6E1]">
          <CardContent className="p-12 text-center">
            <RocketLaunch size={48} className="mx-auto text-[#E8E6E1] mb-4" />
            <p className="text-[#5E6D66]">Aucune donnee d'onboarding disponible</p>
            <p className="text-sm text-[#5E6D66] mt-1">Contactez votre referent pour demarrer le processus</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { steps, global_progress, dossiers_detail, missing_documents, recent_events, organization } = data;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#1A2421]" style={{ fontFamily: 'Manrope' }} data-testid="portal-onboarding-title">
          Mon Onboarding
        </h1>
        <p className="text-[#5E6D66] mt-1 text-sm">
          Suivez votre progression d'adhesion
          {organization?.raison_sociale && ` — ${organization.raison_sociale}`}
        </p>
      </div>

      {/* Global Progress Ring */}
      <Card className="border-[#E8E6E1] overflow-hidden">
        <CardContent className="p-6">
          <div className="flex items-center gap-6">
            {/* SVG Progress Ring */}
            <div className="relative flex-shrink-0" data-testid="onboarding-progress-ring">
              <svg width="100" height="100" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="42" fill="none" stroke="#E8E6E1" strokeWidth="8" />
                <circle
                  cx="50" cy="50" r="42"
                  fill="none"
                  stroke={global_progress === 100 ? '#10b981' : '#2D5A4C'}
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray={`${2 * Math.PI * 42}`}
                  strokeDashoffset={`${2 * Math.PI * 42 * (1 - global_progress / 100)}`}
                  transform="rotate(-90 50 50)"
                  className="transition-all duration-1000"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-2xl font-bold text-[#1A2421]" data-testid="onboarding-progress-value">{global_progress}%</span>
              </div>
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>
                {global_progress === 100 ? 'Onboarding termine !' : 'Progression globale'}
              </h2>
              <p className="text-sm text-[#5E6D66] mt-1">
                {steps.filter(s => s.status === 'completed').length} etape{steps.filter(s => s.status === 'completed').length > 1 ? 's' : ''} sur {steps.length} completee{steps.filter(s => s.status === 'completed').length > 1 ? 's' : ''}
              </p>
              {global_progress === 100 && (
                <Badge className="bg-emerald-100 text-emerald-700 mt-2">Membre actif FTPE</Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Steps Stepper */}
      <Card className="border-[#E8E6E1]">
        <CardContent className="p-6">
          <h2 className="text-sm font-semibold text-[#1A2421] mb-5 uppercase tracking-wide" style={{ fontFamily: 'Manrope' }}>
            Etapes d'onboarding
          </h2>
          <div className="space-y-0" data-testid="onboarding-steps">
            {steps.map((step, idx) => {
              const Icon = STEP_ICONS[step.key] || CheckCircle;
              const style = STATUS_STYLES[step.status];
              const isLast = idx === steps.length - 1;

              return (
                <div key={step.key} className="flex gap-4" data-testid={`onboarding-step-${step.key}`}>
                  {/* Vertical connector */}
                  <div className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ring-4 ${style.ring} ${style.bg} transition-all duration-500`}>
                      {step.status === 'completed' ? (
                        <CheckCircle size={20} className="text-white" weight="bold" />
                      ) : (
                        <Icon size={20} className="text-white" />
                      )}
                    </div>
                    {!isLast && (
                      <div className={`w-0.5 flex-1 min-h-[32px] ${step.status === 'completed' ? 'bg-emerald-300' : 'bg-gray-200'} transition-colors`} />
                    )}
                  </div>
                  {/* Content */}
                  <div className={`pb-6 flex-1 ${isLast ? 'pb-0' : ''}`}>
                    <div className="flex items-center gap-2">
                      <p className={`text-sm font-semibold ${step.status === 'completed' ? 'text-emerald-700' : step.status === 'in_progress' ? 'text-amber-700' : 'text-[#5E6D66]'}`}>
                        {step.label}
                      </p>
                      <Badge className={`text-[10px] ${style.badge}`}>{STATUS_LABELS[step.status]}</Badge>
                    </div>
                    <p className="text-xs text-[#5E6D66] mt-0.5">{step.description}</p>
                    {step.detail && (
                      <p className="text-xs text-[#5E6D66] mt-1 font-medium">{step.detail}</p>
                    )}
                    {step.key === 'documents' && step.progress !== undefined && step.status !== 'completed' && (
                      <div className="flex items-center gap-2 mt-2">
                        <div className="w-32 h-1.5 bg-[#E8E6E1] rounded-full overflow-hidden">
                          <div className="h-full bg-[#2D5A4C] rounded-full transition-all duration-700" style={{ width: `${step.progress}%` }} />
                        </div>
                        <span className="text-[11px] text-[#5E6D66]">{step.progress}%</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Missing Documents Alert */}
      {missing_documents && missing_documents.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <Warning size={20} className="text-amber-600 flex-shrink-0 mt-0.5" weight="fill" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-amber-800">Documents manquants ({missing_documents.length})</h3>
                <p className="text-xs text-amber-700 mt-0.5 mb-3">Ces documents obligatoires sont necessaires pour completer votre onboarding</p>
                <div className="space-y-1.5" data-testid="missing-documents-list">
                  {missing_documents.map((doc, i) => (
                    <div key={`${doc.dossier_id}-${doc.key}`} className="flex items-center gap-2 text-xs">
                      <XCircle size={14} className="text-amber-500 flex-shrink-0" />
                      <span className="text-amber-900 font-medium">{doc.label}</span>
                      <span className="text-amber-600">— {(doc.type_dossier || '').replace(/_/g, ' ')}</span>
                    </div>
                  ))}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3 border-amber-300 text-amber-800 hover:bg-amber-100"
                  onClick={() => navigate('/portail/documents')}
                  data-testid="go-to-documents-btn"
                >
                  <FileText size={14} className="mr-1.5" /> Deposer des documents
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Per-Dossier Detail */}
      {dossiers_detail && dossiers_detail.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-[#1A2421] mb-3 uppercase tracking-wide" style={{ fontFamily: 'Manrope' }}>
            Detail par dossier
          </h2>
          <div className="space-y-3" data-testid="dossier-details">
            {dossiers_detail.map((dd) => {
              const isExpanded = expandedDossier === dd.dossier_id;
              const progressColor = dd.progress === 100 ? 'bg-emerald-500' : dd.progress >= 50 ? 'bg-[#2D5A4C]' : 'bg-amber-500';

              return (
                <Card key={dd.dossier_id} className="border-[#E8E6E1]" data-testid={`dossier-detail-${dd.dossier_id}`}>
                  <CardContent className="p-0">
                    {/* Header */}
                    <button
                      className="w-full flex items-center justify-between p-4 hover:bg-[#F9F8F5] transition-colors text-left"
                      onClick={() => setExpandedDossier(isExpanded ? null : dd.dossier_id)}
                      data-testid={`dossier-toggle-${dd.dossier_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${dd.progress === 100 ? 'bg-emerald-100' : 'bg-[#2D5A4C]/10'}`}>
                          <Folder size={18} className={dd.progress === 100 ? 'text-emerald-600' : 'text-[#2D5A4C]'} />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-[#1A2421]">{dd.numero_dossier}</p>
                          <p className="text-xs text-[#5E6D66]">{(dd.type_dossier || '').replace(/_/g, ' ')}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          <div className="w-20 h-2 bg-[#E8E6E1] rounded-full overflow-hidden">
                            <div className={`h-full rounded-full transition-all duration-500 ${progressColor}`} style={{ width: `${dd.progress}%` }} />
                          </div>
                          <span className="text-xs font-medium text-[#5E6D66] w-8 text-right">{dd.progress}%</span>
                        </div>
                        {dd.progress === 100 ? (
                          <CheckCircle size={18} className="text-emerald-500" weight="fill" />
                        ) : (
                          isExpanded ? <ChevronUp size={16} className="text-[#5E6D66]" /> : <ChevronDown size={16} className="text-[#5E6D66]" />
                        )}
                      </div>
                    </button>

                    {/* Expanded checklist */}
                    {isExpanded && dd.checklist && (
                      <div className="px-4 pb-4 border-t border-[#E8E6E1]">
                        <div className="pt-3 space-y-1.5">
                          {dd.checklist.map((item) => (
                            <div
                              key={item.key}
                              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg ${
                                item.checked ? 'bg-emerald-50' : 'bg-[#F9F8F5]'
                              }`}
                              data-testid={`onboarding-checklist-${dd.dossier_id}-${item.key}`}
                            >
                              {item.checked ? (
                                <CheckCircle size={18} className="text-emerald-600 flex-shrink-0" weight="fill" />
                              ) : (
                                <div className="w-[18px] h-[18px] rounded-full border-2 border-gray-300 flex-shrink-0" />
                              )}
                              <span className={`text-sm flex-1 ${item.checked ? 'text-emerald-700 line-through' : 'text-[#1A2421]'}`}>
                                {item.label}
                              </span>
                              {item.required && !item.checked && (
                                <Badge className="bg-red-50 text-red-600 text-[10px]">Requis</Badge>
                              )}
                              {!item.required && !item.checked && (
                                <Badge className="bg-gray-100 text-gray-500 text-[10px]">Optionnel</Badge>
                              )}
                            </div>
                          ))}
                        </div>
                        <div className="mt-3 text-xs text-[#5E6D66]">
                          {dd.required_checked}/{dd.required_total} documents obligatoires fournis
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent Events Timeline */}
      {recent_events && recent_events.length > 0 && (
        <Card className="border-[#E8E6E1]">
          <CardContent className="p-5">
            <h2 className="text-sm font-semibold text-[#1A2421] mb-4 uppercase tracking-wide" style={{ fontFamily: 'Manrope' }}>
              Activite recente
            </h2>
            <div className="space-y-3" data-testid="recent-events-timeline">
              {recent_events.slice(0, 8).map((evt, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className={`p-1.5 rounded-full mt-0.5 ${evt.type === 'document_uploaded' ? 'bg-blue-100' : 'bg-[#2D5A4C]/10'}`}>
                    {evt.type === 'document_uploaded' ? (
                      <FileText size={14} className="text-blue-600" />
                    ) : (
                      <Folder size={14} className="text-[#2D5A4C]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#1A2421]">{evt.label}</p>
                    {evt.detail && <p className="text-xs text-[#5E6D66] mt-0.5">{evt.detail}</p>}
                  </div>
                  <span className="text-[11px] text-[#5E6D66] flex-shrink-0">
                    {evt.date ? new Date(evt.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : ''}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      {/* Member Notification History */}
      {myNotifications.length > 0 && (
        <Card className="border-[#E8E6E1]">
          <CardContent className="p-5">
            <h2 className="text-sm font-semibold text-[#1A2421] mb-4 uppercase tracking-wide" style={{ fontFamily: 'Manrope' }}>
              Mes notifications
            </h2>
            <div className="space-y-2" data-testid="portal-notifications">
              {myNotifications.map((n) => (
                <div key={n.id} className="flex items-start gap-3 p-3 rounded-lg bg-[#F9F8F5]" data-testid={`portal-notif-${n.id}`}>
                  <div className={`p-1.5 rounded-full mt-0.5 ${
                    n.event_type === 'status_change' ? 'bg-[#2D5A4C]/10' : 'bg-blue-100'
                  }`}>
                    {n.event_type === 'status_change' ? (
                      <RocketLaunch size={14} className="text-[#2D5A4C]" />
                    ) : (
                      <EnvelopeSimple size={14} className="text-blue-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#1A2421]">{n.subject}</p>
                    {n.detail && <p className="text-xs text-[#5E6D66] mt-0.5">{n.detail}</p>}
                  </div>
                  <span className="text-[11px] text-[#5E6D66] flex-shrink-0">
                    {n.sent_at ? new Date(n.sent_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : ''}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
